import { appStorage, appSession } from '../lib/safeStorage';
import axios from 'axios';
import { detailToText } from '../lib/apiError';
import { isClientFacingPath } from '../lib/publicRoutes';
import { invalidateConfig } from './sessionConfigCache';

// S116 — after a successful WRITE to a session-static config resource, drop
// the matching cache key so the next read reflects the change (never stale).
// A login/logout clears everything — a new session must not reuse old config.
function invalidateConfigForRequest(config) {
  const method = (config?.method || '').toLowerCase();
  const url = config?.url || '';
  if (url.includes('/auth/login') || url.includes('/auth/logout')) {
    invalidateConfig();
    return;
  }
  if (method === 'get') return;
  if (url.includes('/company/settings') || url.includes('/company/packages')) {
    invalidateConfig('company/settings');
  }
  if (url.includes('/notifications/preferences')) invalidateConfig('notifications/preferences');
  if (url.includes('/auth/me') || url.includes('/auth/avatar')) invalidateConfig('auth/me');
  if (url.includes('/payments/fee-settings')) invalidateConfig('payments/fee-settings');
  if (url.includes('/subscriptions/') || url.includes('/billing/subscription')) invalidateConfig('billing/subscription');
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
  // ── Phase 2 ROLLBACK (Apr 2026) ───────────────────────────────────
  // `withCredentials: true` was enabled to migrate auth to HttpOnly
  // cookies. It works on the same-origin preview, but production is
  // served from `gotstaffed.com` while the deployed frontend bundle
  // points at `event-prep-center.emergent.host` — a cross-origin pair.
  // With Allow-Origin: * (which the ingress forces) the browser
  // refuses ALL credentialed cross-origin requests → every login,
  // password reset, and authenticated API call fails with "Network
  // Error". We have reverted to Bearer-only auth (the backend's
  // dual-auth path keeps the cookie route alive for future re-attempts).
  // Phase 2 will be re-attempted only after production frontend is
  // rebuilt with REACT_APP_BACKEND_URL=https://gotstaffed.com so the
  // origin matches the API host.
});

// ── User-action tracking (May 2026) ─────────────────────────────────
// To stop the "random blackout / kicked to login mid-typing" bug, we
// only auto-redirect to /login when a 401 is observed CLOSE to a real
// user action (click, keypress, submit). Background polls (notifications
// every 12s, presence pings, etc.) that hit a transient 401 used to
// instantly wipe the session — that was the root cause of the prod
// blackout. Now they fail silently and the user keeps editing.
let lastUserActionAt = Date.now();
const USER_ACTION_WINDOW_MS = 30000; // 30s

if (typeof window !== 'undefined') {
  const bump = () => { lastUserActionAt = Date.now(); };
  // Passive listeners so they don't slow scrolling/typing.
  window.addEventListener('click', bump, { passive: true, capture: true });
  window.addEventListener('keydown', bump, { passive: true, capture: true });
  window.addEventListener('submit', bump, { passive: true, capture: true });
  window.addEventListener('touchstart', bump, { passive: true, capture: true });
}

// Consecutive-401 counter — we ignore a single transient 401 (could
// be a clock-skew JWT race or a single server hiccup). Two 401s on
// user-initiated calls within a 10s window → real session loss → redirect.
let consecutive401s = 0;
let last401At = 0;

// Bearer token interceptor — KEPT during the cutover window so
// existing logged-in users (whose tokens still live in appStorage from
// before the migration) are not kicked out. On their first request the
// backend re-issues an HttpOnly cookie alongside any Bearer auth, then on
// the next request the cookie takes precedence and the appStorage
// token becomes dormant. Phase 3 of the migration removes both this
// interceptor and the appStorage reads/writes everywhere else.
apiClient.interceptors.request.use((config) => {
  // Reading appStorage/appSession THROWS (SecurityError) when the app
  // runs inside a sandboxed iframe — e.g. the /embed/lead-form widget on a
  // client's site. The embed has no auth token anyway, so a failed read just
  // means "no token"; never let it crash the request.
  let token = null;
  try {
    token = appStorage.getItem('token') || appSession.getItem('token');
  } catch (_e) {
    token = null;
  }
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — retry + 401 handling
apiClient.interceptors.response.use(
  (response) => {
    // Successful response → reset the 401 streak.
    consecutive401s = 0;
    // S116 — invalidate session-static config cache on a successful write.
    try { invalidateConfigForRequest(response.config); } catch (_e) { /* noop */ }
    return response;
  },
  async (error) => {
    // FastAPI answers a validation failure with detail = [{loc,msg,type},...].
    // 176 call sites read `...detail || 'Something failed'`, and a JS array
    // coerced into a string is "[object Object]" - which is what the admin
    // saw instead of the field that was actually wrong (Sentry
    // JAVASCRIPT-REACT-G). Normalise HERE, the one place every axios error
    // passes through, rather than at 176 places that each read it.
    // Wrapped: formatting an error must never break error handling.
    try {
      const _d = error && error.response ? error.response.data : null;
      if (_d && typeof _d === 'object' && _d.detail !== undefined && typeof _d.detail !== 'string') {
        _d.detail = detailToText(_d.detail, 'Request failed');
      }
    } catch (e) { /* noop */ }
    const config = error.config;
    const isAuthRoute = config?.url?.includes('/auth/') || config?.url?.includes('/password-reset/');

    // Retry on network errors or 5xx — up to 2 retries.
    // `config._noRetry` lets a specific call opt OUT (e.g. create event:
    // a timed-out POST may have still succeeded server-side, so retrying
    // would create a duplicate).
    if (
      !isAuthRoute &&
      !config?._noRetry &&
      (!error.response || error.response.status >= 500) &&
      (!config._retryCount || config._retryCount < 2)
    ) {
      config._retryCount = (config._retryCount || 0) + 1;
      const delay = config._retryCount * 1000;
      await new Promise(r => setTimeout(r, delay));
      return apiClient(config);
    }

    // Handle 401 — force logout. Hardened (May 2026) against the
    // "mid-typing blackout" bug:
    //   1. Background polls / opt-out calls (`config.skipAuthRedirect`)
    //      never trigger the redirect — they just reject.
    //   2. A 401 that arrives >30s after the last real user action is
    //      treated as a background event and does NOT redirect.
    //   3. A single isolated 401 is ignored. Only the SECOND 401 within
    //      a 10s window triggers the redirect — avoids losing a session
    //      to a single transient hiccup.
    if (error.response?.status === 401 && !isAuthRoute) {
      const now = Date.now();
      const isOptOut = !!config?.skipAuthRedirect;
      const isStaleFromUserAction = (now - lastUserActionAt) > USER_ACTION_WINDOW_MS;

      // A CLIENT IS NEVER SIGNED IN. On a client-facing page a 401 from one of
      // the global providers (notifications, badges, /auth/me) is expected and
      // meaningless — it must never take a paying client to the STAFFED
      // sign-in page. This was live: a client finished a card payment and
      // `/payment-success` bounced them to /login, invisible to the owner
      // because their own browser had an admin session.
      if (isClientFacingPath(window.location.pathname)) {
        return Promise.reject(error);
      }

      // Reset streak counter if previous 401 was too long ago.
      if (now - last401At > 10000) {
        consecutive401s = 0;
      }
      consecutive401s += 1;
      last401At = now;

      if (isOptOut || isStaleFromUserAction) {
        // Background / silent failure — caller can still see the
        // rejection and handle it (or ignore it). The session lives on.
        return Promise.reject(error);
      }

      if (consecutive401s < 2) {
        // Single transient 401 from a user action — don't kill the
        // session yet. Caller's catch block will see the rejection and
        // can show a contextual toast if appropriate.
        return Promise.reject(error);
      }

      // Real, confirmed auth loss — wipe and redirect.
      // eslint-disable-next-line no-console
      console.warn('[apiClient] Auth lost (2nd consecutive 401 from user action) — redirecting to /login');
      try {
        appStorage.removeItem('token');
        appStorage.removeItem('user');
        appSession.removeItem('token');
        appSession.removeItem('user');
      } catch (_e) {
        // Storage blocked (sandboxed iframe) — nothing to clear anyway.
      }
      consecutive401s = 0;
      try { invalidateConfig(); } catch (_e) { /* noop */ }
      // Don't hard-redirect when the user is ALREADY on an unauthenticated auth
      // page (login / signup / password reset). Doing so reloads the page, the
      // global providers (notifications, badges, /auth/me) re-fire, answer 401
      // again, and we redirect once more — an endless reload loop that looks
      // like the preview "blinking / disconnected" and never lets the login
      // form render. On those pages we've already cleared the session; just
      // reject so the form stays up and the user can sign back in.
      const authPath = String(window.location.pathname || '').split('?')[0].replace(/\/+$/, '') || '/';
      const onAuthPage = authPath === '/login'
        || authPath === '/signup'
        || authPath === '/forgot-password'
        || authPath.startsWith('/reset-password');
      if (!onAuthPage) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
